"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import CurrencySwitch from "@/components/CurrencySwitch";
import { useGlassLens, usePressSwell, useRimFollow } from "@/components/glass";
import { backdrop } from "@/lib/glass/style";

const navLinks = [
  { label: "Features", href: "/#features" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Use Cases", href: "/#use-cases" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQs", href: "/#faqs" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);

  // Over the hero video "Get Started" is a lens (refraction in Chromium,
  // blur-only elsewhere). Once the bar turns white it is a solid button.
  useGlassLens(ctaRef, { frost: 3, edge: 10, strength: 8, enabled: !scrolled });
  usePressSwell(ctaRef, { enabled: !scrolled });
  useRimFollow(navRef, ".glass");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      ref={navRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: "0 24px",
        height: 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Only colours animate. backdrop-filter switches on at once: animating
        // it from none re-renders the blur every frame for no visible gain.
        transition: "background-color 0.35s ease, border-color 0.35s ease",
        background: scrolled ? "rgba(255,255,255,0.85)" : "transparent",
        ...backdrop(scrolled ? "blur(20px) saturate(1.4)" : "none"),
        borderBottom: scrolled ? "1px solid rgba(0,0,0,0.06)" : "1px solid transparent",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Logo */}
        <Link
          href="/"
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "1.35rem",
            color: scrolled ? "var(--color-text)" : "#fff",
            textDecoration: "none",
            transition: "color 0.3s",
          }}
        >
          Ethos <span style={{ fontWeight: 300, opacity: 0.7 }}>Virtual</span>
        </Link>

        {/* Desktop Links */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 32,
          }}
          className="nav-desktop"
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.9rem",
                fontWeight: 400,
                color: scrolled ? "var(--color-text-muted)" : "rgba(255,255,255,0.85)",
                textDecoration: "none",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = scrolled ? "var(--color-text)" : "#fff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = scrolled
                  ? "var(--color-text-muted)"
                  : "rgba(255,255,255,0.85)")
              }
            >
              {link.label}
            </a>
          ))}
          <CurrencySwitch scrolled={scrolled} />
          <Link
            href="/login"
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.9rem",
              fontWeight: 500,
              color: scrolled ? "var(--color-text)" : "rgba(255,255,255,0.9)",
              textDecoration: "none",
              transition: "opacity 0.2s",
            }}
          >
            Sign In
          </Link>
          <a
            ref={ctaRef}
            href="/#pricing"
            className={scrolled ? "btn-primary" : "glass glass-clear g-on-dark g-cta g-lift g-press"}
            style={{ padding: "10px 24px", fontSize: "0.88rem", borderRadius: 10 }}
          >
            Get Started
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="nav-mobile-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            display: "none",
            background: "transparent",
            border: "none",
            color: scrolled ? "var(--color-text)" : "#fff",
            cursor: "pointer",
            padding: 4,
          }}
          aria-label="Toggle navigation menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          style={{
            position: "absolute",
            top: 72,
            left: 0,
            right: 0,
            // Solid: this panel hangs off the bar, so a second glass layer
            // would only blur the bar's own content.
            background: "#ffffff",
            boxShadow: "0 18px 30px -18px rgba(0,0,0,0.25)",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "1rem",
                fontWeight: 500,
                color: "var(--color-text)",
                textDecoration: "none",
                padding: "8px 0",
              }}
            >
              {link.label}
            </a>
          ))}
          <div style={{ padding: "8px 0" }}>
            <CurrencySwitch scrolled />
          </div>
          <Link
            href="/login"
            onClick={() => setMobileOpen(false)}
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "1rem",
              fontWeight: 500,
              color: "var(--color-text)",
              textDecoration: "none",
              padding: "8px 0",
            }}
          >
            Sign In
          </Link>
          <a
            href="#pricing"
            className="btn-primary"
            onClick={() => setMobileOpen(false)}
            style={{ textAlign: "center", marginTop: 8 }}
          >
            Get Started
          </a>
        </div>
      )}

    </nav>
  );
}
